import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { AppButton, AppSpin } from '@/components/atoms';
import ConsentFormSection from '@/components/organisms/ConsentFormSection';
import { a2ConsentSchema, r2ConsentSchema, ConsentFormData } from '@/schemas/consentSchema';
import { useConsentDetail } from '@/hooks/main/useConsentDetail';
import { useConsentList } from '@/hooks/main/useConsentList';
import { useUpdateConsentMutation } from '@/hooks/main/useUpdateConsentMutation';
import { useDeleteConsentMutation } from '@/hooks/main/useDeleteConsentMutation';
import { useAppToast } from '@/hooks/useAppToast';
import { useFormChangeGuard } from '@/hooks/useFormChangeGuard';
import { useConsentRouteParams } from '@/hooks/main/useConsentRouteParams';
import useAppModal from '@/hooks/useAppModal';
import PatientPageHeader from '@/pages/main/patient/components/PatientPageHeader';
import ConsentFormBottomBar from '@/pages/main/consent/components/ConsentFormBottomBar';
import { runAhakiCrossCheck } from '@/pages/main/consent/consentAhakiCrossCheck';
import { runLockedTreatmentMonthsCheck } from '@/pages/main/consent/consentLockedTreatmentMonthsCheck';
import { getDefaultConsentFormValues, mapDetailToFormPartial } from '@/pages/main/consent/consentFormMappers';
import {
  applyConsentApiFieldErrors,
  buildA2ConsentRequestBody,
  buildR2ConsentRequestBody,
  makeConsentInvalidHandler,
  runConsentCrossFieldChecks,
} from '@/pages/main/consent/consentFormSubmit';
import ConsentDocumentPrintModal from '@/pages/main/consent/components/ConsentDocumentPrintModal';
import ConsentFormPrintModal from '@/pages/main/consent/components/ConsentFormPrintModal';
import { SERVICE_TYPE_A2, SERVICE_TYPE_R2 } from '@/constants/serviceType';
import { TENANT_ROUTES } from '@/constants/routes';
import { DELETE_CONFIRM_DATA_MESSAGE } from '@/constants/messages';

export const EditConsentPage: React.FC = () => {
  const { tenant, consentId } = useParams();
  const { serviceType: contextServiceType } = useTheme();
  const navigate = useNavigate();
  const toast = useAppToast();
  const { confirm, warning } = useAppModal();

  const confirmFn = useCallback(
    (message: string): Promise<boolean> =>
      new Promise(resolve => {
        confirm({
          content: message,
          okText: 'OK',
          cancelText: 'キャンセル',
          onOk: () => resolve(true),
          onCancel: () => resolve(false),
        });
      }),
    [confirm]
  );

  const alertFn = useCallback(
    (message: string): Promise<void> =>
      new Promise(resolve => {
        warning({
          content: message,
          okText: 'OK',
          onOk: () => resolve(),
        });
      }),
    [warning]
  );
  const [printDocumentType, setPrintDocumentType] = React.useState<1 | 2 | null>(null);
  const [consentFormPrintOpen, setConsentFormPrintOpen] = React.useState(false);

  const serviceType = (contextServiceType?.toLowerCase() as 'r2' | 'a2') || 'r2';
  const isA2 = serviceType.toUpperCase() === SERVICE_TYPE_A2;

  const derived = useConsentRouteParams();

  const latestConsentFilters = useMemo(() => ({
    page: 1,
    per_page: 1,
    sort_type: 'serial_number' as const,
    sort_dir: 'desc' as const,
  }), []);

  const { data: listResponse, isLoading: isListLoading } = useConsentList(derived.patientId, latestConsentFilters);
  const listData = listResponse?.data;
  const patient = listData?.patient;

  const {
    data: sourceConsent,
    isLoading: isDetailLoading,
    isError: isDetailError,
  } = useConsentDetail({
    consentId,
    patientId: derived.patientId,
    enabled: !!consentId && !!derived.patientId,
  });

  const defaultFormValues = useMemo(() => getDefaultConsentFormValues(serviceType), [serviceType]);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    setFocus,
    reset,
    getValues,
    formState: { isSubmitting: formIsSubmitting, isDirty: isFormDirty },
  } = useTrimmedForm<ConsentFormData>({
    resolver: zodResolver(isA2 ? a2ConsentSchema : r2ConsentSchema),
    defaultValues: defaultFormValues,
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    criteriaMode: 'firstError',
  });

  const currentValues = watch();
  const [initialValues, setInitialValues] = React.useState<Partial<ConsentFormData>>(defaultFormValues);
  const guardValues = isFormDirty ? currentValues : (initialValues ?? currentValues);

  const { confirmIfChanged, clearFormChange } = useFormChangeGuard(guardValues, initialValues);

  const hasInitialized = useRef(false);

  useEffect(() => {
    hasInitialized.current = false;
  }, [consentId]);

  useEffect(() => {
    if (!consentId || !derived.patientId || isDetailLoading) {
      return;
    }
    if (hasInitialized.current) {
      return;
    }
    if (!sourceConsent) {
      return;
    }

    const raw = sourceConsent as Record<string, unknown>;
    const mapped = mapDetailToFormPartial(raw, serviceType);
    const vals = { ...getDefaultConsentFormValues(serviceType), ...mapped };
    setInitialValues(vals);
    reset(vals);
    hasInitialized.current = true;
  }, [consentId, derived.patientId, isDetailLoading, sourceConsent, serviceType, reset]);

  const updateMutation = useUpdateConsentMutation({
    patientId: derived.patientId,
    consentId: consentId ?? '',
  });

  const deleteMutation = useDeleteConsentMutation({
    patientId: derived.patientId,
    consentId: consentId ?? '',
  });

  const payloadOpts = useMemo(
    () => ({ isContinueRoute: false, isSpecialContinue: false }),
    []
  );

  const handleBack = useCallback(() => {
    const tenantPrefix = tenant || `${derived.siteType}/${derived.clinicNumber}`;
    const target = `/${tenantPrefix}/consents/${derived.patientId}/list`;
    if (formIsSubmitting || updateMutation.isPending || deleteMutation.isPending) return;

    confirmIfChanged(() => {
      clearFormChange();
      navigate(target);
    });
  }, [
    confirmIfChanged,
    clearFormChange,
    derived.patientId,
    derived.clinicNumber,
    derived.siteType,
    formIsSubmitting,
    updateMutation.isPending,
    deleteMutation.isPending,
    navigate,
    tenant,
  ]);

  const hasVisitsInTreatmentPeriod = !!(sourceConsent as Record<string, unknown> | null)
    ?.has_visits_in_treatment_period;

  const onSubmit = async (data: ConsentFormData) => {
    if (!consentId) return;

    if (
      !await runConsentCrossFieldChecks(data, {
        isContinueRoute: false,
        serviceType,
        setError,
        confirmFn,
        hasVisitsInTreatmentPeriod,
        initialTreatmentContent: initialValues.treatment_content,
      })
    ) {
      return;
    }

    if (!await runLockedTreatmentMonthsCheck(data, {
      serviceType,
      siteType: derived.siteType,
      clinicNumber: derived.clinicNumber,
      patientId: derived.patientId,
      confirmFn,
    })) {
      return;
    }

    const ahakiOk = await runAhakiCrossCheck(data, {
      serviceType,
      siteType: derived.siteType,
      clinicNumber: derived.clinicNumber,
      patientId: derived.patientId,
      confirmFn,
      alertFn,
      onAutoSet: (field, value) => setValue(field, value, { shouldDirty: true }),
    });
    if (!ahakiOk) return;

    const onMutationSuccess = () => {
      setInitialValues(getValues());
      clearFormChange();
    };

    const onMutationError = (error: unknown) => {
      applyConsentApiFieldErrors(error, setError);
      const err = error as { response?: { status?: number; data?: { message?: string; errors?: Record<string, string[]> } } };
      if (err.response?.status === 422 && err.response.data?.errors) {
        toast.error('更新ができませんでした。');
      } else if (err.response?.status === 409) {
        // HIT-7357: backend caught a conflicting あはき counterpart right before writing (two-tab
        // race) — save was rejected, nothing written. Re-run the form's own check by resubmitting.
        toast.error(err.response.data?.message ?? '他の操作により更新されました。もう一度お試しください。');
      }
    };

    const body = isA2
      ? buildA2ConsentRequestBody(data, payloadOpts)
      : buildR2ConsentRequestBody(data, payloadOpts);

    updateMutation.mutate(body, {
      onSuccess: onMutationSuccess,
      onError: onMutationError,
    });
  };

  const handleDelete = useCallback(() => {
    confirm({
      title: '削除確認',
      content: (
        <div>
          <p>{DELETE_CONFIRM_DATA_MESSAGE}</p>
        </div>
      ),
      okButtonProps: { danger: true },
      onOk: async () => {
        deleteMutation.mutate();
      },
      okText: 'OK',
      cancelText: 'キャンセル',
    });
  }, [confirm, deleteMutation]);

  const isBusy = formIsSubmitting || updateMutation.isPending || deleteMutation.isPending;

  if (isListLoading || isDetailLoading) {
    return (
      <div className="flex justify-center p-10">
        <AppSpin size="large" />
      </div>
    );
  }

  if (isDetailError || !sourceConsent) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 p-10">
        <p className="text-base text-gray-700">同意書が見つかりません。</p>
        <AppButton
          type="link"
          className="p-0 h-auto"
          onClick={() => {
            const tenantPrefix = tenant || `${derived.siteType}/${derived.clinicNumber}`;
            navigate(`/${tenantPrefix}/consents/${derived.patientId}/list`);
          }}
        >
          一覧に戻る
        </AppButton>
      </div>
    );
  }

  const formId = 'consent-edit-form';

  return (
    <div className="patient-edit-page">
      {derived.patientId && (
        <PatientPageHeader
          patient={patient}
          patientId={derived.patientId}
          tenant={tenant || ''}
          navigate={navigate}
          activeKey="consents"
          disabled={isBusy}
        />
      )}

      <div>
        <form
          id={formId}
          onSubmit={handleSubmit(onSubmit, makeConsentInvalidHandler(toast, setFocus))}
        >
          <ConsentFormSection
            control={control}
            watch={watch}
            setValue={setValue}
            serviceType={serviceType}
            formMode="edit"
            isContinue={false}
            patient={patient}
            disabled={isBusy}
            onTreatmentReportClick={() => {
              if (!tenant || !derived.patientId || !consentId) return;
              navigate(TENANT_ROUTES.CONSENTS.TREATMENT_RECORD_PRINT(tenant, derived.patientId, consentId));
            }}
          />
        </form>
      </div>

      <ConsentFormBottomBar
        mode="edit"
        isSubmitting={isBusy}
        isDeleting={deleteMutation.isPending}
        formId={formId}
        onBack={handleBack}
        onDelete={handleDelete}
        canPrint={!!consentId}
        onClickThankYouPrint={() => setPrintDocumentType(1)}
        onClickRequestPrint={() => setPrintDocumentType(2)}
        onClickConsentFormPrint={() => setConsentFormPrintOpen(true)}
      />

      {!!consentId && printDocumentType && (
        <ConsentDocumentPrintModal
          consentId={Number(consentId)}
          documentTypeId={printDocumentType}
          isOpen={true}
          onClose={() => setPrintDocumentType(null)}
        />
      )}

      {!!consentId && (
        <ConsentFormPrintModal
          consentId={Number(consentId)}
          siteType={derived.siteType}
          clinicNumber={derived.clinicNumber}
          serviceTypeHeader={isA2 ? SERVICE_TYPE_A2 : SERVICE_TYPE_R2}
          isOpen={consentFormPrintOpen}
          onClose={() => setConsentFormPrintOpen(false)}
        />
      )}
    </div>
  );
};
